import { classInstance } from '@/shared/helpers/helpers';

export class Counter {
	private counter: HTMLElement;

	private inputSelector = '.counter__value';
	private minusSelector = '.counter__button--decrement';
	private plusSelector = '.counter__button--increment';
	private removeSelector = '.counter__button--remove';

	private input: HTMLInputElement;
	private minusButton: HTMLButtonElement;
	private plusButton: HTMLButtonElement;
	private removeButton: HTMLButtonElement | null = null;
	private min: number = 0;
	private max: number = 0;
	public value: number = 0;

	constructor(element: HTMLElement | null) {
		if (!element) return;
		this.counter = element;

		this.init();
	}

	init(): void {
		this.input = this.counter.querySelector(this.inputSelector);
		this.minusButton = this.counter.querySelector(this.minusSelector);
		this.plusButton = this.counter.querySelector(this.plusSelector);
		this.removeButton = this.counter.querySelector(this.removeSelector);

		if (!this.input) return;

		this.min = parseInt(this.input.min, 10);
		this.max = parseInt(this.input.max, 10);
		this.value = parseInt(this.input.value, 10) || 0;

		classInstance.set(this.counter, { counter: this });

		this.setButtonsState();
		this.bindEvents();
	}

	toggleRemoveButtonState = (): void => {
		if (!this.removeButton) return;

		const shouldShowRemove = this.value === this.min;

		if (shouldShowRemove) {
			this.removeButton.removeAttribute('disabled');
		} else {
			this.removeButton.setAttribute('disabled', '');
		}
	};

	setButtonsState = (): void => {
		[this.minusButton, this.plusButton].forEach((btn) => {
			btn?.removeAttribute('disabled');
		});

		if (this.value === this.min) {
			this.minusButton?.setAttribute('disabled', '');
		}

		if (this.value === this.max) {
			this.plusButton?.setAttribute('disabled', '');
		}

		this.toggleRemoveButtonState();
	};

	setValue = (newValue: number): void => {
		if (!this.input) return;

		if (newValue < this.min) this.value = this.min;
		else if (newValue > this.max) this.value = this.max;
		else this.value = newValue;

		this.input.value = `${this.value}`;
		this.input.dispatchEvent(new CustomEvent('change'));

		this.setButtonsState();
	};

	handleClick = (e: MouseEvent): void => {
		const target = e.target as HTMLElement;
		const button = target.closest('[data-type]') as HTMLElement | null;
		if (!button) return;

		const { type } = button.dataset;

		if (type === 'dec') this.setValue(this.value - 1);
		if (type === 'inc') this.setValue(this.value + 1);

		if (type === 'remove') {
			this.counter?.dispatchEvent(
				new CustomEvent('removeCounter', {
					detail: { counter: this },
				}),
			);
		}
	};

	handleInput = (e: Event): void => {
		const target = e.target as HTMLInputElement;
		const { value } = target;

		if (value && !value.match(/^-?[0-9]\d*?$/)) {
			target.value = `${this.value}`;
			return;
		}

		this.setValue(parseInt(value, 10));
	};

	handleBlur = (e: FocusEvent): void => {
		const target = e.target as HTMLInputElement;

		if (target.value) return;

		this.setValue(this.min);
	};

	bindEvents(): void {
		this.counter?.addEventListener('click', this.handleClick);
		this.input.addEventListener('input', this.handleInput);
		this.input.addEventListener('blur', this.handleBlur);
	}

	destroy(): void {
		this.counter?.removeEventListener('click', this.handleClick);
		this.input?.removeEventListener('input', this.handleInput);
		this.input?.removeEventListener('blur', this.handleBlur);
	}
}
